import BalanceText from 'js/components/BalanceText'
import HowItWorks from 'js/components/HowItWorks/HowItWorks'
import SocialProof from 'js/components/SocialProof/SocialProof'
import Testimonials from 'js/components/Testimonials/Testimonials'
import WhatIsLifesum from 'js/components/WhatIsLifesum/WhatIsLifesum'
import Image from 'js/components/DatoRichText/DatoImage'
import withSSR from 'js/components/withSSR/withSSR'
import { getDatoLocale, mergeFallback, runQuery } from 'js/dato'
import { datoLocaleToLanguage } from 'js/datoLocales'
import React, { useEffect, useState } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import ErrorPage from '../ErrorPage'
import PartnerPage from './PartnerPage'
import { PartnerPageFields } from './types/PartnerPageFields'
import { useDispatch, useSelector } from 'react-redux'
import { setLanguage } from 'js/store/localeSlice'
import { language, languages } from 'js/locale'
import { setSessionItem } from 'js/storeManager'
import Spinner from 'js/components/Spinner/Spinner'
import {
  fetchPricing,
  setCoupon,
  selectPricingConfig,
} from 'js/store/pricingSlice'
import { FormattedMessage } from 'react-intl'
import convert from 'htmr'
import { AppDispatch } from 'js/app'
import {
  selectIsPremium,
  selectSubscriptionExpiryTime,
} from 'js/store/userSlice'
import { isSubscriptionExpiringWithinDays } from 'js/utils/partnerRedeemUtils'

const query = gql`
  fragment PartnerPageFields on PartnerPageRecord {
    _locales
    customHeaderHtml
    logo {
      responsiveImage {
        srcSet
        sizes
        src
      }
    }
    logoLink
    headerImage {
      url
    }
    headerIngress
    header
    ctaButton
    bodyHeading
    body
    customContentHtml
    coupon
  }
  query PartnerPageQuery($slug: String!, $locale: SiteLocale) {
    page: partnerPage(filter: { slug: { eq: $slug } }, locale: $locale) {
      ...PartnerPageFields
    }
    fallback: partnerPage(filter: { slug: { eq: $slug } }, locale: en) {
      ...PartnerPageFields
    }
  }
`

type PartnerLogoProps = {
  logoLink: string
  responsiveImage?: { src: string } | null // ts hack quickfix
}

const PartnerLogo = ({ responsiveImage, logoLink }: PartnerLogoProps) => {
  if (logoLink) {
    return (
      <a href={logoLink}>
        <Image
          {...responsiveImage}
          src={responsiveImage?.src as string}
          width="100"
          sizes="135px"
        />
      </a>
    )
  }

  return (
    <Image
      {...responsiveImage}
      src={responsiveImage?.src as string}
      width="100"
      sizes="135px"
    />
  )
}

type Props = {
  data: PartnerPageFields
  isLoading: boolean
  error: {}
  params: {
    slug: string
  }
}

const CmsPartnerPage = ({ data, isLoading, error, params }: Props) => {
  const dispatch = useDispatch() as AppDispatch
  const pricingConfig = useSelector(selectPricingConfig())
  const [searchParams, _] = useSearchParams()
  const [clientSideCoupon, setClientSideCoupon] = useState<string | null>(null)
  const [hasError, setHasError] = useState(false)
  const [isExpiredError, setIsExpiredError] = useState(false)
  const location = useLocation()
  const isPremium = useSelector(selectIsPremium)
  const expiryTime = useSelector(selectSubscriptionExpiryTime)
  const isExpiringWithin14Days = isSubscriptionExpiringWithinDays(
    expiryTime,
    14,
  )

  // Find if there are discounts and return highest one
  const getInitialPlan = (): 1 | 3 | 6 | 12 | 24 | undefined => {
    if (pricingConfig?.products && pricingConfig.products.length > 0) {
      const sortedProducts = [...pricingConfig.products].sort((a, b) => {
        const discountA = a.originalPrice
          ? ((a.originalPrice - a.price) / a.originalPrice) * 100
          : 0
        const discountB = b.originalPrice
          ? ((b.originalPrice - b.price) / b.originalPrice) * 100
          : 0
        return discountB - discountA
      })

      if (sortedProducts[0]) {
        const selectedPlan = sortedProducts[0].duration as 1 | 3 | 6 | 12 | 24
        return selectedPlan
      }
    }

    return 12
  }

  useEffect(() => {
    const maybeUrlLanguage =
      location.pathname.match(/^\/(\w{2})\/partner/i)?.[1]
    const urlLanguageCode =
      languages.find((language) => language === maybeUrlLanguage) ||
      language.ENGLISH

    dispatch(setLanguage(urlLanguageCode, false))

    setSessionItem('bannerShown', true)
    const coupon = data?.coupon || searchParams.get('coupon')
    if (coupon) {
      setClientSideCoupon(coupon)
      dispatch(fetchPricing(coupon)).catch(({ errorDetail }) => {
        setIsExpiredError(errorDetail.includes('expired'))
        setHasError(true)
      })
      dispatch(setCoupon(coupon))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, searchParams, data])

  // this should only happen when developing the page with the dev-server
  if (!data && isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )
  }

  if (error) {
    return <ErrorPage error={error} />
  }

  return (
    <>
      {data.customHeaderHtml ? convert(data.customHeaderHtml) : null}
      <PartnerPage
        partner="KeyExperience"
        partnerLogo={
          <PartnerLogo
            responsiveImage={data.logo?.responsiveImage}
            logoLink={data.logoLink as NonNullable<typeof data.logoLink>}
          />
        }
        heroGradient="linear-gradient(162deg,rgba(19, 20, 27, 0.2),rgba(19, 20, 27, 0.6))"
        heroImage={`${data.headerImage?.url}?w=1600`}
        heroImageMobile={`${data.headerImage?.url}?w=1200`}
        heroHeading={
          <BalanceText>
            <span>{data.header}</span>
          </BalanceText>
        }
        heroSubHeading={data.headerIngress}
        ctaButton={
          <Link
            data-uid="claim_offer_button"
            className="mx-auto inline-block cursor-pointer
            rounded-8 bg-LSBrand px-32 py-16 text-center text-14
            font-demibold text-white shadow-subtle transition hover:bg-LSBrandHover focus:bg-LSBrandHover active:bg-LSBrandActive"
            to={
              isPremium && isExpiringWithin14Days
                ? clientSideCoupon
                  ? 'account'
                  : 'redeem'
                : clientSideCoupon
                ? 'account'
                : 'redeem'
            }
          >
            <span>{data.ctaButton}</span>
          </Link>
        }
        rootUrl={`/partner/${params.slug}`}
        languages={
          data._locales.length === 1
            ? []
            : data._locales.map(
                (locale) => datoLocaleToLanguage[locale] ?? locale,
              )
        }
        initialPlan={getInitialPlan()}
      >
        <div>
          <div className="mx-auto mb-72 mt-40 max-w-1024 text-left lg:text-center">
            {hasError && (
              <div className="mx-24 mb-24 rounded-8 border-2 border-secondary-red px-24 py-32 type-24 lg:mx-64">
                <h2 className="mb-12 font-demibold text-secondary-red">
                  We couldn’t load your offer
                </h2>
                <p>
                  {isExpiredError
                    ? 'Unfortunately, this offer has expired.'
                    : 'We seem to be having technical difficulties. Please try again shortly.'}
                </p>
                {!isExpiredError && (
                  <button
                    className="mt-24 rounded-full bg-white px-32 py-12 text-secondary-blue type-17-demibold-caps"
                    type="button"
                    onClick={() => {
                      document.location.reload()
                    }}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}
            <div className="mx-24 lg:mx-auto lg:w-4/5">
              <h2 className="mb-24 mt-64 text-left type-40 lg:mb-36 lg:text-center lg:type-56">
                <BalanceText>
                  <span>{data.bodyHeading}</span>
                </BalanceText>
              </h2>
              <div className="leading-body type-24 lg:mx-48">
                <div
                  className="mb-24"
                  dangerouslySetInnerHTML={{ __html: data.body as string }}
                />
              </div>
            </div>
          </div>

          <div className="pb-24 text-left lg:pb-48 lg:text-center">
            <div className="mx-auto px-20 lg:px-32">
              <h2 className="mb-24 type-32 lg:mb-36 lg:type-40">
                <FormattedMessage id="how_lifesum_works" />
              </h2>
              <HowItWorks />
            </div>
          </div>

          <div className="c-premium-usps c-premium-usps--bright">
            <div className="o-width-limiter">
              <h2 className="mb-24 type-40 lg:mb-36 lg:text-center lg:type-56">
                <FormattedMessage id="GEPage.whatsIncludedHeading" />
              </h2>
              <br />
              <WhatIsLifesum />
            </div>
          </div>
          <SocialProof />
          <div className="o-width-limiter o-width-limiter--narrow">
            <div className="-mt-96 mb-96 lg:ml-96 lg:mr-96 xl:ml-64 xl:mr-64">
              <Testimonials />
            </div>
          </div>

          <div className="relative z-10 mx-auto -mt-48 mb-96 max-w-1024 px-24 text-center lg:-mt-24 lg:mb-128">
            <h2 className="mb-24 type-40 lg:mb-36 lg:text-center lg:type-56">
              <BalanceText>
                <span>{data.bodyHeading}</span>
              </BalanceText>
            </h2>
            <Link
              data-uid="claim_offer_button"
              className="c-button c-button--premium"
              to={
                isPremium && isExpiringWithin14Days
                  ? clientSideCoupon
                    ? 'account'
                    : 'redeem'
                  : clientSideCoupon
                  ? 'account'
                  : 'redeem'
              }
            >
              <span>{data.ctaButton}</span>
            </Link>
          </div>
          <div className="relative z-10 mx-auto -mt-48 mb-96 max-w-1024 px-24 text-center lg:-mt-24 lg:mb-128">
            {data.customContentHtml ? convert(data.customContentHtml) : null}
          </div>
        </div>
      </PartnerPage>
    </>
  )
}

CmsPartnerPage.getInitialProps = ({
  params,
  location,
  language,
  isPreview,
}) => {
  const locale = getDatoLocale(language)
  const slug =
    params.slug ||
    location.pathname.replace(/^\/\w{2}\//g, '').replace(/\//g, '')

  return runQuery(query, { slug, locale }, isPreview).then(
    ({ page, fallback }) => {
      if (page === null) {
        return
      }

      return mergeFallback(page, fallback)
    },
  )
}

export default withSSR(CmsPartnerPage)
